import React from "react";
import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Edit3, X, Check, Star } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useUpdateFeedback } from "@/hooks/api-hooks";

const editFeedbackSchema = z.object({
  responseText: z.string().min(10, "Please provide meaningful feedback (at least 10 characters)"),
  rating: z.number().min(1).max(5).optional(),
  isAnonymous: z.boolean(),
});

type EditFeedbackFormData = z.infer<typeof editFeedbackSchema>;

interface Feedback {
  id: string;
  responseText: string;
  responseRating?: number;
  isAnonymous: boolean;
  submittedAt: string;
  feedbackCycle?: {
    id: string;
    name: string;
  };
}

interface EditFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  feedback: Feedback | null;
  onSuccess?: () => void;
}

export function EditFeedbackModal({
  isOpen,
  onClose,
  feedback,
  onSuccess
}: EditFeedbackModalProps) {
  const [selectedRating, setSelectedRating] = React.useState<number>(0);
  const updateFeedbackMutation = useUpdateFeedback();

  const form = useForm<EditFeedbackFormData>({
    resolver: zodResolver(editFeedbackSchema),
    defaultValues: {
      responseText: feedback?.responseText || "",
      rating: feedback?.responseRating || undefined,
      isAnonymous: feedback?.isAnonymous || false,
    },
  });

  // Update form when feedback changes
  React.useEffect(() => {
    if (feedback) {
      form.reset({
        responseText: feedback.responseText,
        rating: feedback.responseRating || undefined,
        isAnonymous: feedback.isAnonymous,
      });
      setSelectedRating(feedback.responseRating || 0);
    }
  }, [feedback, form]);

  const onSubmit = async (data: EditFeedbackFormData) => {
    if (!feedback) return;

    const updateData = {
      id: feedback.id,
      responses: [
        { questionId: 'feedback-text', text: data.responseText },
        ...(data.rating ? [{ questionId: 'feedback-rating', text: data.rating.toString(), rating: data.rating }] : [])
      ],
      isAnonymous: data.isAnonymous,
    };

    updateFeedbackMutation.mutate(updateData, {
      onSuccess: () => {
        toast.success('Feedback updated successfully!');
        onSuccess?.();
        onClose();
        form.reset();
      },
      onError: (error: any) => {
        console.error("Error updating feedback:", error);
        toast.error(`Failed to update feedback: ${error?.message || 'Please try again.'}`);
      }
    });
  };

  const handleClose = () => {
    form.reset();
    setSelectedRating(feedback?.responseRating || 0);
    onClose();
  };

  if (!feedback) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg bg-card text-card-foreground border border-border shadow-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Edit3 className="h-5 w-5 text-white" />
            </div>
            <DialogTitle className="text-xl font-bold text-foreground">
              Edit Feedback
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Feedback Info */}
          <div className="p-4 bg-muted/30 rounded-lg border border-border">
            <div className="text-sm text-muted-foreground">
              {feedback.feedbackCycle?.name && (
                <p>Cycle: <span className="font-medium text-foreground">{feedback.feedbackCycle.name}</span></p>
              )}
              <p>Submitted: <span className="font-medium text-foreground">{new Date(feedback.submittedAt).toLocaleDateString()}</span></p>
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="responseText"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Feedback *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Share your feedback, observations, suggestions for improvement..."
                        rows={5}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Rating */}
              <div className="space-y-2">
                <FormLabel>Overall Rating (Optional)</FormLabel>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => {
                        setSelectedRating(rating);
                        form.setValue('rating', rating);
                      }}
                      className={`p-1 rounded transition-colors ${
                        rating <= selectedRating
                          ? 'text-yellow-500 hover:text-yellow-600'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Star className={`h-6 w-6 ${rating <= selectedRating ? 'fill-current' : ''}`} />
                    </button>
                  ))}
                  {selectedRating > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedRating(0);
                        form.setValue('rating', undefined);
                      }}
                      className="ml-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {selectedRating > 0 && (
                  <p className="text-xs text-muted-foreground">
                    You rated: {selectedRating} star{selectedRating !== 1 ? 's' : ''}
                  </p>
                )}
              </div>

              <FormField
                control={form.control}
                name="isAnonymous"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base font-medium">
                        Anonymous Feedback
                      </FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Hide your identity when giving this feedback
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={updateFeedbackMutation.isPending}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateFeedbackMutation.isPending}
                  className="bg-primary hover:bg-primary/90"
                >
                  {updateFeedbackMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Update Feedback
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}